/**
 * listingPhotoResolver
 *
 * Single, shared definition of the display-time photo fallback order for a listing.
 *
 * The chain is: real dealer photo(s) -> model AI image -> existing placeholder.
 * It is N-deep: if any candidate fails to load at render, the consumer advances to
 * the next candidate (see useImageFallback). This module is PURE: no I/O, no React,
 * no module-level state, no network. That keeps it trivially unit-testable and means
 * the dead-URL memo can live entirely in the hook without making this impure.
 *
 * See spec: .claude/specs/broken-photo-model-fallback/ (design Component 1).
 */

/**
 * Select a model's primary image URL from a list of vehicle_model_images rows.
 * Mirrors the rule in databaseService.transformSupabasePayloadToRecommendation
 * (is_default_display first, then the first available row). Deliberately duplicated
 * across runtimes (this JS util, the Deno _shared/pickModelImageUrl.ts, and the
 * worker-local JS copy) because they cannot share a module.
 *
 * @param {Array<{image_url?: string, is_default_display?: boolean}>|null|undefined} modelImages
 * @returns {string|null} the chosen image_url, or null if none usable.
 */
export const pickModelImageUrl = (modelImages) => {
  if (!Array.isArray(modelImages) || modelImages.length === 0) {
    return null;
  }
  const isUsable = (img) => img && typeof img.image_url === 'string' && img.image_url.length > 0;

  const defaultImage = modelImages.find((img) => isUsable(img) && img.is_default_display);
  if (defaultImage) {
    return defaultImage.image_url;
  }
  const firstUsable = modelImages.find(isUsable);
  return firstUsable ? firstUsable.image_url : null;
};

/**
 * Build the ordered, de-duplicated list of candidate photo URLs for a listing.
 *
 * @param {object} params
 * @param {string|null|undefined} params.dealerPhotoUrl   primary dealer photo (e.g. listing.photo_url).
 * @param {Array<string>|string|null|undefined} params.dealerPhotoLinks additional dealer photos (carousel).
 * @param {string|null|undefined} params.modelImageUrl    resolved model AI image URL.
 * @param {string|null|undefined} params.placeholderUrl   the surface's existing last-resort placeholder.
 * @returns {Array<string>} ordered, de-duplicated, non-empty candidate URLs.
 *          Order: dealer photo(s) -> model image -> placeholder. Null/empty dropped.
 */
export const resolveListingPhotoCandidates = ({
  dealerPhotoUrl,
  dealerPhotoLinks,
  modelImageUrl,
  placeholderUrl,
} = {}) => {
  const ordered = [];

  // 1. Dealer photo(s) - always preferred when they load.
  if (typeof dealerPhotoUrl === 'string') {
    ordered.push(dealerPhotoUrl);
  }
  if (Array.isArray(dealerPhotoLinks)) {
    for (const link of dealerPhotoLinks) {
      if (typeof link === 'string') {
        ordered.push(link);
      }
    }
  } else if (typeof dealerPhotoLinks === 'string') {
    ordered.push(dealerPhotoLinks);
  }

  // 2. Model AI image.
  if (typeof modelImageUrl === 'string') {
    ordered.push(modelImageUrl);
  }

  // 3. Last-resort placeholder.
  if (typeof placeholderUrl === 'string') {
    ordered.push(placeholderUrl);
  }

  // Drop empties and de-duplicate, preserving order.
  const seen = new Set();
  const result = [];
  for (const url of ordered) {
    const trimmed = url.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
};
